import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import {
    LayoutDashboard, Users, DollarSign, CalendarDays,
    MessageSquare, ShieldCheck, Settings, LogOut, Menu, X,
    ChevronRight, Globe, Bike, ExternalLink, Bell, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

const ROLE_LABELS = {
    admin: 'Administrator', ketua_rt: 'Ketua RT', wakil: 'Wakil RT',
    sekretaris: 'Sekretaris', bendahara: 'Bendahara',
    sie_keamanan: 'Sie Keamanan', sie_kebersihan: 'Sie Kebersihan',
    sie_pemuda: 'Sie Pemuda', sie_perlengkapan: 'Sie Perlengkapan',
    sie_umum: 'Sie Umum', warga: 'Warga'
};
const ROLE_COLORS = {
    admin: 'bg-red-100 text-red-700', ketua_rt: 'bg-blue-100 text-blue-700',
    wakil: 'bg-sky-100 text-sky-700', sekretaris: 'bg-purple-100 text-purple-700',
    bendahara: 'bg-green-100 text-green-700', warga: 'bg-slate-100 text-slate-600'
};

export default function Layout() {
    const { user, logout, hasRole } = useAuth();
    const { lang, toggleLang, t } = useLang();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        toast.success('Berhasil keluar');
        navigate('/login');
    };

    const canKeliling = hasRole('bendahara', 'ketua_rt', 'wakil', 'admin');
    const canAnnouncement = hasRole('sekretaris', 'ketua_rt', 'wakil');

    const navItems = [
        { to: '/dashboard', icon: LayoutDashboard, label: t.dashboard },
        { to: '/citizens', icon: Users, label: t.citizens },
        { to: '/finance', icon: DollarSign, label: t.finance },
        ...(canKeliling ? [{ to: '/keliling', icon: Bike, label: 'Mode Keliling' }] : []),
        { to: '/meetings', icon: CalendarDays, label: t.meetings },
        { to: '/complaints', icon: MessageSquare, label: t.complaints },
        ...(canAnnouncement ? [{ to: '/announcements', icon: Bell, label: 'Pengumuman' }] : []),
    ];
    const adminItems = [
        { to: '/users', icon: ShieldCheck, label: t.userMgmt, roles: ['ketua_rt', 'wakil', 'sekretaris', 'admin'] },
        { to: '/settings', icon: Settings, label: t.settings, roles: ['ketua_rt', 'wakil', 'sekretaris', 'admin'] },
        { to: '/logs', icon: Activity, label: 'Log Aktivitas', roles: ['admin'] },
    ];

    const SidebarContent = () => (
        <>
            {/* Logo area */}
            <div className="px-4 py-4 border-b border-slate-100 bg-emerald-950/5">
                <div className="flex items-center gap-3">
                    <img src="/logo_surabaya.svg" alt="Pemkot Surabaya" className="w-10 h-10 object-contain flex-shrink-0 drop-shadow-sm" />
                    <div className="min-w-0">
                        <div className="inline-flex items-center gap-1.5 mb-0.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                            <p className="text-xs font-bold text-emerald-900 uppercase tracking-wider leading-none">RT 06 / RW 01</p>
                        </div>
                        <p className="text-xs font-semibold text-slate-600 truncate">Menanggal, Gayungan</p>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-0.5">
                {navItems.map(item => (
                    item.external ? (
                        <a key={item.to} href={item.to} target="_blank" rel="noopener noreferrer"
                           className="sidebar-nav-item" onClick={() => setSidebarOpen(false)}>
                            <item.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="flex-1 truncate">{item.label}</span>
                            <ChevronRight className="w-3.5 h-3.5 opacity-40" />
                        </a>
                    ) : (
                        <NavLink key={item.to} to={item.to}
                            className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={`nav-${item.to.replace('/', '')}`}>
                            <item.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="flex-1 truncate">{item.label}</span>
                        </NavLink>
                    )
                ))}

                {adminItems.some(i => hasRole(...i.roles)) && (
                    <div className="pt-3">
                        <p className="label-overline px-3 pb-2">Admin</p>
                        {adminItems.map(item => hasRole(...item.roles) && (
                            <NavLink key={item.to} to={item.to}
                                className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`nav-${item.to.replace('/', '')}`}>
                                <item.icon className="w-4 h-4 flex-shrink-0" />
                                <span className="flex-1 truncate">{item.label}</span>
                            </NavLink>
                        ))}
                    </div>
                )}
            </nav>

            {/* Public link */}
            <div className="px-3 py-2 border-t border-slate-100">
                <a href="/public" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-3 py-2 rounded-md text-xs font-medium text-slate-500 hover:text-primary hover:bg-primary/5 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Dasbor Publik Transparansi</span>
                </a>
            </div>

            {/* User info */}
            <div className="px-3 py-3 border-t border-slate-100">
                <div className="flex items-center gap-3 px-2 py-2 rounded-md">
                    <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-primary text-white text-xs font-bold">
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${ROLE_COLORS[user?.role] || 'bg-slate-100 text-slate-600'}`}>
                            {ROLE_LABELS[user?.role] || user?.role}
                        </span>
                    </div>
                </div>
                <button onClick={handleLogout} data-testid="logout-btn"
                    className="w-full mt-1 flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                    <LogOut className="w-4 h-4" />
                    {t.logout}
                </button>
            </div>
        </>
    );

    return (
        <div className="App">
            {/* Desktop Sidebar */}
            <aside className="sidebar hidden md:flex flex-col">
                <SidebarContent />
            </aside>

            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
                    <aside className="absolute left-0 top-0 h-full w-64 bg-white flex flex-col shadow-xl">
                        <SidebarContent />
                    </aside>
                </div>
            )}

            {/* Main */}
            <main className="main-content">
                {/* Top header */}
                <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-4 md:px-6 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button className="md:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
                            onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle">
                            <Menu className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-2">
                            <span className="hidden sm:inline-block w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                            <span className="text-sm font-bold text-slate-800 tracking-tight">Sistem Informasi RT 06</span>
                            
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:block text-xs text-slate-500 font-medium">
                            {new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                        <button onClick={toggleLang} data-testid="lang-toggle"
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                            <Globe className="w-3.5 h-3.5" />
                            {lang.toUpperCase()}
                        </button>
                    </div>
                </header>

                {/* Page content */}
                <div className="p-4 md:p-6 max-w-7xl mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
